/**
 * Vendor detail — S14. Read-only page; "Edit" sends to `$vendorId.edit`.
 *
 * Surfaces (top → bottom), per `prototype/purchases/vendor-detail.html`:
 *
 *   1. Cover: avatar, name + status pills, edit/archive actions, info grid
 *      (Contact / Address / GST & PAN).
 *   2. Stat strip — We owe / YTD spend / Avg payment / Reliability.
 *   3. Tabs: Activity / Bills / POs / Payments / Ledger / Documents.
 *      Activity carries the prototype body grid; secondary tabs keep focused
 *      placeholder states until their dedicated APIs exist.
 *
 * @see prototype/purchases/vendor-detail.html — V1.1 vendor detail baseline
 */
import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Alert, ConfirmDialog, TabsRoot, TabsList, TabsTrigger, TabsContent } from "@flint/ui";
import { STEP_UP_ACTIONS } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryOptions } from "../../../../lib/session.js";
import { useStepUpGate } from "../../../../features/auth/index.ts";
import {
  buildVendorDetailModel,
  VendorBillsPanel,
  VendorCover,
  VendorDetailEmptyTab,
  VendorDetailOverview,
  VendorDetailStatStrip,
  VendorLedgerPanel,
} from "../../../../features/vendors/index.ts";
import { coolingOffStatus } from "../../../../features/vendors/lib/format.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
// Pulls the .s32 dialog + .field-affix + .btn-primary/secondary styles
// so the VendorBankDialog below renders without needing its own stylesheet.
// CSS is page-root-level (not scoped under .bank-edit-page) — see
// apps/web/src/routes/(workspace)/bank-accounts/bank-edit-page.css.
import "../../cash/bank-accounts/bank-edit-page.css";

export const Route = createFileRoute("/(workspace)/procurement/vendors/$vendorId/")({
  component: VendorDetailPage,
});

function VendorDetailPage() {
  const { vendorId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const vendor = trpc.vendors.get.useQuery({ id: vendorId });
  const bills = trpc.bills.list.useQuery(
    { vendorId, limit: 100 },
    { enabled: vendor.data !== undefined },
  );
  const me = useQuery(meQueryOptions);
  const companyState = me.data?.company?.state ?? "";
  const model = useMemo(
    () => (vendor.data ? buildVendorDetailModel(vendor.data, bills.data, companyState) : null),
    [vendor.data, bills.data, companyState],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState<
    "activity" | "bills" | "pos" | "payments" | "ledger" | "documents"
  >("activity");

  const archive = trpc.vendors.archive.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.vendors.list.invalidate(),
        utils.vendors.counts.invalidate(),
        utils.vendors.get.invalidate({ id: vendorId }),
      ]);
      void navigate({ to: "/procurement/vendors", search: {} });
    },
  });

  // ── Change-bank dialog state (ADR-0008) ────────────────
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankModalError, setBankModalError] = useState<string | null>(null);
  const [draftAccount, setDraftAccount] = useState("");
  const [draftIfsc, setDraftIfsc] = useState("");
  const [draftIfscLookup, setDraftIfscLookup] = useState<{
    bank: string;
    branch: string;
    city: string;
    state: string;
  } | null>(null);
  const [draftIfscErr, setDraftIfscErr] = useState<string | null>(null);
  const [draftIfscLoading, setDraftIfscLoading] = useState(false);
  const stepUpGate = useStepUpGate(STEP_UP_ACTIONS.VENDORS_CHANGE_BANK_DETAILS);

  const draftIfscOk = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(draftIfsc);
  const draftAcctOk = /^\d{9,18}$/.test(draftAccount);
  const draftReady = draftAcctOk && draftIfscOk;

  const openBankModal = () => {
    setDraftAccount("");
    setDraftIfsc("");
    setDraftIfscLookup(null);
    setDraftIfscErr(null);
    setBankModalError(null);
    setBankModalOpen(true);
  };
  const closeBankModal = () => setBankModalOpen(false);

  const changeBank = trpc.vendors.changeBankDetails.useMutation({
    onSuccess: async () => {
      await utils.vendors.get.invalidate({ id: vendorId });
      closeBankModal();
    },
    onError: (err) => setBankModalError(err.message),
  });

  const fetchDraftIfsc = async () => {
    if (!draftIfscOk) return;
    setDraftIfscLoading(true);
    setDraftIfscErr(null);
    try {
      const r = await utils.ifsc.lookup.fetch({ ifsc: draftIfsc });
      setDraftIfscLookup({ bank: r.bank, branch: r.branch, city: r.city, state: r.state });
    } catch (err) {
      setDraftIfscLookup(null);
      setDraftIfscErr(err instanceof Error ? err.message : "IFSC lookup failed.");
    } finally {
      setDraftIfscLoading(false);
    }
  };

  const saveBankModal = async () => {
    if (!draftReady) return;
    setBankModalError(null);
    try {
      await stepUpGate.execute(
        () =>
          changeBank.mutateAsync({
            id: vendorId,
            bankAccount: draftAccount.trim(),
            ifsc: draftIfsc.trim().toUpperCase(),
          }),
        {
          title: `Change pay-to bank for ${vendor.data?.name ?? "vendor"}`,
          description:
            "Founder OTP is required. After save, a 24-hour cooling-off window blocks outbound payments to the new bank details.",
        },
      );
    } catch (err) {
      setBankModalError(err instanceof Error ? err.message : "Couldn't save bank details.");
    }
  };

  const baseCrumbs = [
    { label: "Procurement", to: "/" },
    { label: "Vendors", to: "/procurement/vendors" },
  ];

  if (vendor.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Vendor" }]}>
        <p className="text-muted">Loading vendor…</p>
      </WorkspaceShell>
    );
  }
  if (vendor.error) {
    const isNotFound = vendor.error.data?.code === "NOT_FOUND";
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Vendor" }]}>
        <Alert
          intent="bad"
          title={isNotFound ? "Vendor not found." : "Couldn't load vendor."}
          description={vendor.error.message}
        />
      </WorkspaceShell>
    );
  }
  if (!vendor.data) return null;
  if (!model) return null;
  const data = vendor.data;
  const cooling = coolingOffStatus(data.bankDetailsChangedAt);

  return (
    <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: data.name }]}>
      <div className="space-y-5">
        <VendorCover
          vendor={data}
          model={model}
          archiving={archive.isPending}
          onArchive={() => setConfirmOpen(true)}
        />

        {archive.error ? (
          <Alert intent="bad" title="Archive failed" description={archive.error.message} />
        ) : null}

        <VendorDetailStatStrip metrics={model.metrics} />

        {/* ── Tabs ────────────────────────────────────────────────── */}
        <TabsRoot value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList aria-label="Vendor sections">
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="bills" count={model.billCount} countTone="warn">
              Bills
            </TabsTrigger>
            <TabsTrigger value="pos" count={model.poCount}>
              POs
            </TabsTrigger>
            <TabsTrigger value="payments" count={model.paymentCount}>
              Payments
            </TabsTrigger>
            <TabsTrigger value="ledger">Ledger</TabsTrigger>
            <TabsTrigger value="documents" count={model.documentCount}>
              Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="pt-4">
            <VendorDetailOverview
              vendor={data}
              model={model}
              coolingOff={cooling}
              onChangeBank={openBankModal}
            />
          </TabsContent>
          <TabsContent value="bills" className="pt-4">
            <VendorBillsPanel model={model} />
          </TabsContent>
          <TabsContent value="pos" className="pt-4">
            <VendorDetailEmptyTab
              title="POs land later in V1.x"
              description="Purchase orders + 3-way match are scheduled after the bills vertical."
            />
          </TabsContent>
          <TabsContent value="payments" className="pt-4">
            <VendorDetailEmptyTab
              title="Payments wire up alongside the cooling-off check"
              description="The payments vertical also enforces the 24-hour bank-detail-change cooling-off (system-decisions §14)."
            />
          </TabsContent>
          <TabsContent value="ledger" className="pt-4">
            <VendorLedgerPanel vendorId={vendorId} vendorName={data.name} />
          </TabsContent>
          <TabsContent value="documents" className="pt-4">
            <VendorDetailEmptyTab
              title="Documents land with S3 attachments"
              description="Vendor-uploaded documents (PAN copy, MSME cert, bank stamp) ride on the S3 attachment seam (ADR-0009; supersedes architecture §8's R2 default)."
            />
          </TabsContent>
        </TabsRoot>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Archive ${data.name}?`}
        description="Archived vendors stay in your records and the audit trail. They're hidden from the active list and the dropdowns on new bills. You can browse archived vendors from the “Archived” tab."
        confirmLabel="Archive vendor"
        cancelLabel="Cancel"
        intent="danger"
        pending={archive.isPending}
        onConfirm={async () => {
          await archive.mutateAsync({ id: vendorId });
        }}
      />

      {bankModalOpen ? (
        <VendorBankDialog
          vendorName={data.name}
          draftAccount={draftAccount}
          setDraftAccount={setDraftAccount}
          draftIfsc={draftIfsc}
          onDraftIfscChange={(v) => {
            setDraftIfsc(v);
            if (draftIfscLookup || draftIfscErr) {
              setDraftIfscLookup(null);
              setDraftIfscErr(null);
            }
          }}
          draftIfscLookup={draftIfscLookup}
          draftIfscErr={draftIfscErr}
          draftIfscLoading={draftIfscLoading}
          draftIfscOk={draftIfscOk}
          draftAcctOk={draftAcctOk}
          draftReady={draftReady}
          mutationError={bankModalError}
          isPending={changeBank.isPending || stepUpGate.busy}
          onFetch={fetchDraftIfsc}
          onSave={() => void saveBankModal()}
          onCancel={closeBankModal}
        />
      ) : null}
      {stepUpGate.dialog}
    </WorkspaceShell>
  );
}

// ── Vendor change-bank dialog ──────────────────────────────────
// Mirrors the bank-accounts /edit credentials dialog. Reuses the
// `.s32-scrim` / `.s32` page-root styles from
// `apps/web/src/routes/(workspace)/bank-accounts/bank-edit-page.css`
// (the CSS is loaded by every workspace page once any cash-module
// surface mounts).

interface VendorBankDialogProps {
  vendorName: string;
  draftAccount: string;
  setDraftAccount: (v: string) => void;
  draftIfsc: string;
  onDraftIfscChange: (v: string) => void;
  draftIfscLookup: {
    bank: string;
    branch: string;
    city: string;
    state: string;
  } | null;
  draftIfscErr: string | null;
  draftIfscLoading: boolean;
  draftIfscOk: boolean;
  draftAcctOk: boolean;
  draftReady: boolean;
  mutationError: string | null;
  isPending: boolean;
  onFetch: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function VendorBankDialog(props: VendorBankDialogProps) {
  return (
    <div
      className="s32-scrim show"
      onClick={(e) => {
        if (e.target === e.currentTarget && !props.isPending) props.onCancel();
      }}
    >
      <div
        className="s32 show"
        role="dialog"
        aria-modal="true"
        aria-label={`Change pay-to bank for ${props.vendorName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="s32-head">
          <div className="s32-head-text">
            <h2>Change pay-to bank</h2>
            <p>
              Vendor: <strong>{props.vendorName}</strong>. Requires Founder OTP + a 24-hour
              cooling-off before any outbound payment can use the new bank details (ADR-0008).
            </p>
          </div>
          <button
            type="button"
            className="s32-close"
            onClick={props.onCancel}
            aria-label="Close"
            disabled={props.isPending}
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="s32-body">
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              New account number <span className="req">*</span>
            </label>
            <input
              type="text"
              className="field-input"
              value={props.draftAccount}
              onChange={(e) => props.setDraftAccount(e.target.value.replace(/\s+/g, ""))}
              placeholder="50100123456789"
              maxLength={18}
              autoFocus
              disabled={props.isPending}
            />
            <span
              className={`field-help${props.draftAccount && !props.draftAcctOk ? " is-error" : ""}`}
            >
              9–18 digits.
              {props.draftAccount && !props.draftAcctOk
                ? " Bank account numbers are digits only."
                : ""}
            </span>
          </div>
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              New IFSC code <span className="req">*</span>
            </label>
            <div className="field-affix">
              <input
                type="text"
                className="field-input"
                value={props.draftIfsc}
                onChange={(e) => props.onDraftIfscChange(e.target.value.toUpperCase())}
                placeholder="HDFC0001247"
                maxLength={11}
                disabled={props.isPending}
              />
              <button
                type="button"
                className="field-affix-action"
                onClick={props.onFetch}
                disabled={!props.draftIfscOk || props.draftIfscLoading || props.isPending}
                title={
                  props.draftIfscOk
                    ? "Fetch bank + branch from RBI directory"
                    : "Enter a valid 11-character IFSC first"
                }
              >
                {props.draftIfscLoading ? "Fetching…" : "Fetch"}
              </button>
            </div>
            <span
              className={`field-help${
                props.draftIfscLookup ? " is-ok" : props.draftIfscErr ? " is-error" : ""
              }`}
            >
              {props.draftIfscLookup
                ? `✓ ${props.draftIfscLookup.bank} · ${props.draftIfscLookup.branch}, ${props.draftIfscLookup.city}`
                : props.draftIfscErr
                  ? props.draftIfscErr
                  : `11-char RBI code · ${props.draftIfsc.length}/11${props.draftIfscOk ? " · click Fetch to verify" : ""}.`}
            </span>
          </div>

          {props.mutationError ? (
            <div
              style={{
                marginBottom: 14,
                padding: 12,
                background: "#fdf2f8",
                border: "1px solid #fce7f3",
                borderRadius: 6,
                color: "#db2777",
                fontSize: 12,
                lineHeight: 1.55,
              }}
            >
              <strong>Couldn't save.</strong> {props.mutationError}
            </div>
          ) : null}

          <div
            style={{
              padding: 12,
              background: "#fefce8",
              border: "1px solid #fef9c3",
              borderRadius: 6,
              color: "#854d0e",
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <strong>Founder OTP is required before commit.</strong> After save, the 24-hour
            cooling-off starts immediately — no outbound payment to this vendor can use the new bank
            details until it ends.
          </div>
        </div>
        <div
          style={{
            padding: 14,
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={props.onCancel}
            disabled={props.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={props.onSave}
            disabled={!props.draftReady || props.isPending}
          >
            {props.isPending ? "Saving…" : "Save bank details"}
          </button>
        </div>
      </div>
    </div>
  );
}
